"use client";

import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import {
  isValidPhoneNumber as isValidPhoneNumberStrict,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../utils/axiosInstance";
import { getRecaptchaToken } from "../utils/getcapthcatoken";
import { useTranslation } from "next-i18next";

const initial = {
  fullName: "",
  phone: "",
  companyName: "",
  companyEmail: "",
  companyWebsite: "",
  services: [],
};

const servicesList = [
  { id: 1, name_en: "Advertising", name_ar: "الإعلان" },
  { id: 2, name_en: "Branding", name_ar: "العلامة التجارية" },
  { id: 3, name_en: "Content Marketing", name_ar: "تسويق المحتوى" },
  { id: 4, name_en: "Designing", name_ar: "التصميم" },
  { id: 5, name_en: "Digital Marketing", name_ar: "التسويق الرقمي" },
  {
    id: 6,
    name_en: "E-Commerce Management",
    name_ar: "إدارة المتاجر الالكترونية",
  },
  {
    id: 7,
    name_en: "Email Marketing",
    name_ar: "التسويق عبر البريد الالكتروني",
  },
  { id: 8, name_en: "Event Management", name_ar: "إدارة المناسبات" },
  { id: 9, name_en: "Events/Exhibitions", name_ar: "المناسبات والفعاليات" },
  { id: 10, name_en: "Exhibition Services", name_ar: "خدمات الفعاليات" },
  { id: 11, name_en: "EZ Digital", name_ar: "أي زد ديجيتال" },
  { id: 12, name_en: "Lead Generation", name_ar: "توليد العملاء" },
  { id: 13, name_en: "Mobile Applications", name_ar: "تطبيقات الموبايل" },
  { id: 14, name_en: "Photography", name_ar: "التصوير الفوتوغرافي" },
  { id: 15, name_en: "Public Relations", name_ar: "العلاقات العامة" },
  { id: 16, name_en: "Retail Marketing", name_ar: "تسويق البيع بالتجزئة" },
  { id: 17, name_en: "SEM", name_ar: "تسويق محركات البحث" },
  { id: 18, name_en: "SEO", name_ar: "تحسين محركات البحث" },
  {
    id: 19,
    name_en: "Social Media Marketing",
    name_ar: "تسويق عبر مواقع التواصل الاجتماعي",
  },
  { id: 20, name_en: "Video Production", name_ar: "إنتاج الفيديو" },
  {
    id: 21,
    name_en: "Web Development Services",
    name_ar: "خدمات تطوير مواقع الويب",
  },
  { id: 22, name_en: "Web Services", name_ar: "خدمات الويب" },
  {
    id: 23,
    name_en: "WhatsApp and SMS Marketing",
    name_ar: "تسويق الواتساب والرسائل النصة",
  },
  {
    id: 24,
    name_en: "WhatsApp for Business API",
    name_ar: "تطبيقات واجهة واتساب الاعمال",
  },
  { id: 25, name_en: "ZOHO Solutions", name_ar: "حلول زوهو" },
];

const phoneLabels = {
  ...en,
  country: "Country",
  phone: "Phone number",
};
export default function FreeConsultationForm({ onSuccess }) {
  const { t } = useTranslation("common");
  const router = useRouter();
  const lang = router.locale || "en";

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [formToken, setFormToken] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [websiteTrap, setWebsiteTrap] = useState("");

  useEffect(() => {
    const loadToken = async () => {
      try {
        const r = await axiosInstance.get(`/form-token?lang=${lang}`);
        setFormToken(r?.data?.formToken || "");
      } catch (e) {
        console.error("Failed to load form token", e);
        setFormToken("");
      }
    };

    loadToken();
  }, [lang]);

  const serviceOptions = useMemo(
    () =>
      servicesList.map((s) => ({
        value: lang === "ar" ? s.name_ar : s.name_en,
        label: lang === "ar" ? s.name_ar : s.name_en,
      })),
    [lang],
  );

  const selectedServices = useMemo(
    () =>
      serviceOptions.filter((option) => form.services.includes(option.value)),
    [serviceOptions, form.services],
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === "fullName") {
      nextValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({ ...prev, phone: value || "" }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const getValidatedMobilePhone = (phone) => {
    if (!phone) return { ok: false, reason: "empty", normalized: "" };

    try {
      const parsed = parsePhoneNumberFromString(phone);

      if (!parsed || !parsed.isValid()) {
        return { ok: false, reason: "invalid", normalized: "" };
      }

      const type = parsed.getType();

      // Allow only mobile-capable numbers
      if (type !== "MOBILE" && type !== "FIXED_LINE_OR_MOBILE") {
        return {
          ok: false,
          reason: "non_mobile",
          normalized: parsed.number || "",
          type,
        };
      }

      return {
        ok: true,
        reason: "",
        normalized: parsed.number,
        type,
      };
    } catch {
      return { ok: false, reason: "invalid", normalized: "" };
    }
  };

  const getNormalizedPhone = () => {
    const result = getValidatedMobilePhone(form.phone);
    return result.ok ? result.normalized : "";
  };

  const handleServicesChange = (selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((item) => item.value)
      : [];

    setForm((prev) => ({
      ...prev,
      services: values,
    }));

    setErrors((prev) => ({
      ...prev,
      services: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formToken) {
      toast.error("Please wait a moment and try again.");
      return;
    }

    const nextErrors = {};

    const fullNameRegex = /^[A-Za-z\s]+$/;

    const websiteRegex =
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

    if (!form.fullName.trim()) {
      nextErrors.fullName = "Please Enter Full Name.";
    } else if (!fullNameRegex.test(form.fullName.trim())) {
      nextErrors.fullName = "Full Name must contain letters only.";
    }
    if (!form.companyName)
      nextErrors.companyName = "Please Enter Company Name.";
    if (!form.companyWebsite)
      nextErrors.companyWebsite = "Please Enter Company Website.";
    else if (!websiteRegex.test(form.companyWebsite)) {
      nextErrors.companyWebsite = "Please enter a valid website URL.";
    }
    const phoneCheck = getValidatedMobilePhone(form.phone);

    if (!form.phone) {
      nextErrors.phone = "Please Enter Phone Number.";
    } else if (!phoneCheck.ok) {
      if (phoneCheck.reason === "invalid") {
        nextErrors.phone = "Please enter a valid phone number.";
      } else if (phoneCheck.reason === "non_mobile") {
        nextErrors.phone =
          "Only mobile numbers are allowed (no landline, toll-free, or VoIP).";
      }
    }
    if (!form.companyEmail) {
      nextErrors.companyEmail = "Please Enter Email.";
    } else if (!emailRegex.test(form.companyEmail)) {
      nextErrors.companyEmail = "Invalid email address.";
    }
    if (!form.services.length) nextErrors.services = "Please Select Service.";

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken("consultation_submit");
      const timeOnPageMs = Date.now() - startedAt;

      const phoneCheck = getValidatedMobilePhone(form.phone);

      const payload = {
        fullName: form.fullName,
        phone: phoneCheck.normalized,
        services: form.services,
        companyName: form.companyName,
        companyEmail: form.companyEmail,
        companyWebsite: form.companyWebsite,
        formToken,
        timeOnPageMs,
        website: websiteTrap,
        recaptchaToken,
      };

      // Submitted to staging api
      const res = await axiosInstance.post(
        `/websiteform/staging/new-lead?lang=${lang}`,
        payload,
      );

      toast.success(
        res?.data?.message ||
          "Thank you for your request! We’ve received consultation request successfully. Kindly check your email and book a slot for your consultation.",
      );

      setForm(initial);
      setErrors({});
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "12px",
      border: `1px solid ${errors?.services ? "red" : "#D9D9D9"}`,
      boxShadow: "none",
      backgroundColor: "#fff",
      paddingLeft: "4px",
      paddingRight: "4px",
      transition: "all 0.2s ease",
      "&:hover": {
        border: `1px solid ${errors?.services ? "red" : "#D9D9D9"}`,
      },
      ...(state.isFocused && {
        border: `1px solid ${errors?.services ? "red" : "#D9D9D9"}`,
      }),
      "@media (min-width: 1024px)": {
        minHeight: "52px",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "6px 8px",
      gap: "6px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9CA3AF",
      fontSize: "14px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#9CA3AF",
      padding: "6px",
      "&:hover": {
        color: "#9CA3AF",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#FFF1F3",
      borderRadius: "9999px",
      paddingLeft: "4px",
      paddingRight: "2px",
      margin: "2px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#FF4D67",
      fontSize: "12px",
      fontWeight: 500,
      padding: "4px 6px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#FF4D67",
      borderRadius: "9999px",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#FFD9DF",
        color: "#FF4D67",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      border: "1px solid #E5E7EB",
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: "6px",
      paddingBottom: "6px",
      maxHeight: "220px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#FFEEF1"
        : state.isFocused
          ? "#FFF6F7"
          : "#fff",
      color: "#111827",
      cursor: "pointer",
      fontSize: "14px",
      padding: "10px 14px",
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      fontSize: "14px",
      color: "#6B7280",
    }),
  };

  const normalizedPreview = getNormalizedPhone();
  return (
    <div className="w-full box-border max-w-[520px] bg-white rounded-[20px] border border-[#E6E6E6] p-4 lg:p-6 shadow-sm">
      <h2 className="text-[20px] sm:text-[26px] lg:text-[28px] leading-tight font-semibold text-[#000] mt-0">
        {t("form.title")}{" "}
        <span className="text-[#FF4D67] text-[28px] sm:text-[36px] lg:text-[45px] font-bold">
          {t("form.title1")}
        </span>
        <br />
        {t("form.title2")}
      </h2>
      <style jsx global>{`
        .custom-phone-input .PhoneInputInput {
          flex: 1;
          width: 100%;
          min-width: 0;
          background: transparent;
          color: #000;
          border: 0;
          outline: none;
          font-size: 16px;
          padding: 0;
        }
      `}</style>
      <form
        onSubmit={handleSubmit}
        className="mt-6 space-y-5 consultation_form"
      >
        <div className="self-stretch flex flex-col lg:flex-row items-start justify-start gap-4 max-w-full">
          <div className="w-full flex-1 flex flex-col gap-1">
            <input
              type="text"
              name="fullName"
              placeholder={t("form.name")}
              className="w-full box-border text-[16px] h-[42px] lg:h-[52px] rounded-[12px] px-4"
              style={{ border: "1px solid #D9D9D9" }}
              value={form.fullName}
              onChange={handleInputChange}
              inputMode="text"
              autoComplete="name"
            />
            {errors?.fullName && (
              <p className="m-0 text-[14px]" style={{ color: "red" }}>
                {errors.fullName}
              </p>
            )}
          </div>

          <div className="w-full flex-1 flex flex-col gap-1">
            <input
              type="text"
              name="companyName"
              placeholder={t("form.company")}
              className="w-full box-border text-[16px] h-[42px] lg:h-[52px] rounded-[12px] px-4"
              style={{ border: "1px solid #D9D9D9" }}
              value={form.companyName}
              onChange={handleInputChange}
            />
            {errors?.companyName && (
              <p className="m-0 text-[14px]" style={{ color: "red" }}>
                {errors.companyName}
              </p>
            )}
          </div>
        </div>

        <div className="self-stretch flex flex-col lg:flex-row items-start justify-start gap-4 max-w-full">
          <div className="w-full flex-1 flex flex-col gap-1">
            <input
              type="text"
              name="companyWebsite"
              placeholder={t("form.website")}
              className="w-full box-border text-[16px] h-[42px] lg:h-[52px] rounded-[12px] px-4"
              style={{ border: "1px solid #D9D9D9" }}
              value={form.companyWebsite}
              onChange={handleInputChange}
            />
            {errors?.companyWebsite && (
              <p className="m-0 text-[14px]" style={{ color: "red" }}>
                {errors.companyWebsite}
              </p>
            )}
          </div>

          <div className="w-full flex-1 flex flex-col gap-1">
            <input
              type="email"
              name="companyEmail"
              placeholder={t("form.email")}
              className="w-full box-border text-[16px] h-[42px] lg:h-[52px] rounded-[12px] px-4"
              style={{ border: "1px solid #D9D9D9" }}
              value={form.companyEmail}
              onChange={handleInputChange}
            />
            {errors?.companyEmail && (
              <p className="m-0 text-[14px]" style={{ color: "red" }}>
                {errors.companyEmail}
              </p>
            )}
          </div>
        </div>

        <div className="w-full flex-1 flex flex-col gap-1" dir="ltr">
          <div className="custom-phone-input">
            <PhoneInput
              id="phone"
              name="phone"
              defaultCountry="AE"
              international
              withCountryCallingCode
              labels={phoneLabels}
              value={form.phone}
              onChange={handlePhoneChange}
              className="w-full box-border border border-solid border-[#D9D9D9] h-[42px] lg:h-[52px] rounded-[12px] px-4"
            />
          </div>

          {errors?.phone ? (
            <p className="m-0 text-[14px]" style={{ color: "red" }}>
              {errors.phone}
            </p>
          ) : (
            <p className="m-0 text-[14px]" style={{ color: "orange" }}>
              {normalizedPreview
                ? `Will send as: ${normalizedPreview}`
                : "Use international format +971xx, +91xx, etc."}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Select
            isMulti
            isSearchable
            name="services"
            options={serviceOptions}
            value={selectedServices}
            onChange={handleServicesChange}
            placeholder={t("form.service")}
            styles={customSelectStyles}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isRtl={lang === "ar"}
            noOptionsMessage={() =>
              lang === "ar" ? "لا توجد نتائج" : "No services found"
            }
          />
          {errors?.services && (
            <p className="m-0 text-[14px]" style={{ color: "red" }}>
              {errors.services}
            </p>
          )}
        </div>

        <input
          type="text"
          name="website"
          value={websiteTrap}
          onChange={(e) => setWebsiteTrap(e.target.value)}
          autoComplete="off"
          tabIndex={-1}
          style={{
            position: "absolute",
            left: "-10000px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        />

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full h-[42px] lg:h-[56px] rounded-full bg-[#FF4D67] text-white cursor-pointer hover:opacity-90 transition disabled:cursor-not-allowed disabled:opacity-70 book_consultation_form"
        >
          {loading ? t("form.btnLoad") : t("form.btnTxt")}
        </button>
      </form>
    </div>
  );
}
